package com.schoolconnect.app.repository.examresult

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.TeacherExamResultListResponse
import com.schoolconnect.app.datasource.network.response.ExamResultsListResponse
import com.schoolconnect.app.datasource.network.response.ExamResultsSummaryResponse
import com.schoolconnect.app.datasource.network.response.ExamResultBulkEntryResponse
import com.schoolconnect.app.datasource.network.response.ExamImportResponse
import com.schoolconnect.app.datasource.network.response.ExamInsightsResponse
import com.schoolconnect.app.datasource.network.response.ExamPublishResponse
import com.schoolconnect.app.datasource.network.request.ExamResultBulkEntryRequest
import com.schoolconnect.app.util.scLog
import kotlinx.serialization.json.JsonObject
import com.schoolconnect.app.repository.common.RepositoryCache
import com.schoolconnect.app.repository.common.tenantCacheKey
import com.schoolconnect.app.repository.exam.ExamRepository
import kotlinx.coroutines.CoroutineScope

sealed interface ExamResultScope {
    data object All : ExamResultScope
    data class School(val schoolId: String) : ExamResultScope
    data class AcademicYear(val academicYearId: String) : ExamResultScope
    data class ClassSection(val classSectionId: String) : ExamResultScope
    data class Exam(val examId: String) : ExamResultScope
    data class Student(val studentId: String) : ExamResultScope
    data class User(val userId: String) : ExamResultScope
}

object ExamResultRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ExamResultRepository"
    private val teacherPageCache = RepositoryCache<TeacherExamResultListResponse>()

    private fun invalidateTeacherProgress(examId: String, operation: String) {
        teacherPageCache.clear()
        ExamRepository.invalidateTeacherPage()
        scLog("$TAG invalidateTeacherProgress exam=$examId operation=$operation")
    }

    suspend fun loadExamResults(examId: String): Result<ExamResultsListResponse> =
        KtorApiClient.getExamResults(examId.trim())
            .onSuccess { scLog("$TAG loadExamResults success exam=$examId count=${it.count ?: 0}") }
            .onFailure { scLog("$TAG loadExamResults failure exam=$examId", it) }

    suspend fun loadExamResultsSummary(examId: String): Result<ExamResultsSummaryResponse> =
        KtorApiClient.getExamResultsSummary(examId.trim())
            .onSuccess { scLog("$TAG loadExamResultsSummary success exam=$examId count=${it.count ?: 0}") }
            .onFailure { scLog("$TAG loadExamResultsSummary failure exam=$examId", it) }

    suspend fun loadExamInsights(examId: String): Result<ExamInsightsResponse> =
        KtorApiClient.getExamInsights(examId.trim())
            .onSuccess { scLog("$TAG loadExamInsights success exam=$examId students=${it.totalStudents ?: 0}") }
            .onFailure { scLog("$TAG loadExamInsights failure exam=$examId", it) }

    suspend fun publishExamResults(examId: String): Result<ExamPublishResponse> =
        KtorApiClient.publishExamResults(examId.trim())
            .onSuccess {
                scLog("$TAG publishExamResults success exam=$examId status=${it.status.orEmpty()}")
                invalidateTeacherProgress(examId, "publish")
            }
            .onFailure { scLog("$TAG publishExamResults failure exam=$examId", it) }

    suspend fun downloadImportTemplate(examId: String): Result<ByteArray> =
        KtorApiClient.downloadExamImportTemplate(examId.trim())
            .onSuccess { scLog("$TAG downloadImportTemplate success exam=$examId bytes=${it.size}") }
            .onFailure { scLog("$TAG downloadImportTemplate failure exam=$examId", it) }

    suspend fun downloadResultsExport(examId: String): Result<ByteArray> =
        KtorApiClient.downloadExamResultsExport(examId.trim())
            .onSuccess { scLog("$TAG downloadResultsExport success exam=$examId bytes=${it.size}") }
            .onFailure { scLog("$TAG downloadResultsExport failure exam=$examId", it) }

    suspend fun importExamResults(examId: String, fileName: String, bytes: ByteArray): Result<ExamImportResponse> =
        KtorApiClient.importExamResults(examId.trim(), fileName.trim(), bytes)
            .onSuccess {
                scLog("$TAG importExamResults success exam=$examId processed=${it.processedRecords ?: 0}")
                invalidateTeacherProgress(examId, "import")
            }
            .onFailure { scLog("$TAG importExamResults failure exam=$examId", it) }

    suspend fun saveExamResults(classSectionId: String, request: ExamResultBulkEntryRequest): Result<ExamResultBulkEntryResponse> =
        KtorApiClient.bulkEnterExamResults(classSectionId.trim(), request)
            .onSuccess {
                scLog("$TAG saveExamResults success exam=${request.examId} processed=${it.processedCount ?: 0}")
                invalidateTeacherProgress(request.examId, "bulk-save")
            }
            .onFailure { scLog("$TAG saveExamResults failure exam=${request.examId}", it) }

    suspend fun loadTeacherPage(
        search: String?,
        status: String? = null,
        classSectionId: String? = null,
        pageIndex: Int,
        pageSize: Int,
        sortBy: String?,
        order: String?,
        refreshScope: CoroutineScope
    ): Result<TeacherExamResultListResponse> {
        val key = tenantCacheKey("teacher-exam-results:${search.orEmpty()}:${status.orEmpty()}:${classSectionId.orEmpty()}:$pageIndex:$pageSize:${sortBy.orEmpty()}:${order.orEmpty()}")
        return teacherPageCache.load(refreshScope, TAG, "loadTeacherPage", key) { KtorApiClient.getTeacherExamResults(
        search = search,
        status = status,
        classSectionId = classSectionId,
        pageIndex = pageIndex,
        pageSize = pageSize,
        sortBy = sortBy,
        order = order
    ).onSuccess { response ->
        scLog("$TAG loadTeacherPage success page=${pageIndex + 1} items=${response.items.orEmpty().size} total=${response.counters?.total ?: 0}")
    }.onFailure { throwable ->
        scLog("$TAG loadTeacherPage failure page=${pageIndex + 1}", throwable)
    } }
    }

    suspend fun loadParentResults(
        studentId: String,
        search: String?,
        pageIndex: Int,
        pageSize: Int,
        sortBy: String?,
        order: String?
    ): Result<JsonObject> {
        val normalizedStudentId = studentId.trim()
        require(normalizedStudentId.isNotBlank()) { "studentId must not be blank" }
        val request = com.schoolconnect.app.feature.common.PageRequest(pageIndex, pageSize)
        return KtorApiClient.getParentExamResults(
            studentId = normalizedStudentId,
            limit = request.safePageSize,
            offset = request.offset,
            search = search,
            sortBy = sortBy,
            order = order
        ).onSuccess { response ->
            scLog("$TAG loadParentResults success student=$normalizedStudentId page=${request.safePageIndex + 1} size=${request.safePageSize} count=${response["count"]}")
        }.onFailure { throwable -> scLog("$TAG loadParentResults failure student=$normalizedStudentId page=${request.safePageIndex + 1}", throwable) }
    }

    suspend fun loadParentSummary(studentId: String): Result<JsonObject> =
        KtorApiClient.getParentExamResultsSummary(studentId.trim())
            .onSuccess { scLog("$TAG loadParentSummary success student=$studentId") }
            .onFailure { throwable -> scLog("$TAG loadParentSummary failure student=$studentId", throwable) }

    suspend fun loadParentDetail(examId: String, studentId: String): Result<JsonObject> =
        KtorApiClient.getParentExamResultDetail(examId.trim(), studentId.trim())
            .onSuccess { scLog("$TAG loadParentDetail success exam=$examId student=$studentId") }
            .onFailure { throwable -> scLog("$TAG loadParentDetail failure exam=$examId student=$studentId", throwable) }

}
